import { Link } from '@tanstack/react-router';
import { ArrowLeft, Power, Pencil, Trash2, RefreshCw, Wifi, WifiOff, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

interface DeviceHeaderProps {
  device: any;
  isOnline: boolean;
  refreshInterval: number | null;
  toggleRefresh: () => void;
  openEditModal: () => void;
  openDeleteModal: () => void;
}

export function DeviceHeader({
  device,
  isOnline,
  refreshInterval,
  toggleRefresh,
  openEditModal,
  openDeleteModal,
}: DeviceHeaderProps) {
  return (
    <div className="flex flex-col space-y-4">
      {/* Navigation and Title */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" asChild className="shrink-0">
          <Link to="/dashboard/devices">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold truncate">{device.name || 'Unnamed Device'}</h1>
            <div className="flex items-center gap-2">
              <Badge variant={isOnline ? 'default' : 'secondary'} className="shrink-0">
                {isOnline ? <Wifi className="h-3 w-3 mr-1" /> : <WifiOff className="h-3 w-3 mr-1" />}
                {isOnline ? 'Online' : 'Offline'}
              </Badge>
              {device.status && (
                <Badge variant="outline" className="shrink-0">
                  Active
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="font-mono">{device.serial_number}</span>
            {device.mac_address && <span className="font-mono">{device.mac_address}</span>}
            {device.location_name && <span>📍 {device.location_name}</span>}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={refreshInterval ? 'secondary' : 'outline'}
            onClick={toggleRefresh}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshInterval ? 'animate-spin' : ''}`} />
            {refreshInterval ? 'Auto-refresh on' : 'Auto-refresh off'}
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" variant={isOnline ? 'destructive' : 'default'} className="gap-2">
            <Power className="h-4 w-4" />
            {isOnline ? 'Power Off' : 'Power On'}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="gap-2">
                <MoreHorizontal className="h-4 w-4" />
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              <DropdownMenuItem onClick={openEditModal} className="gap-2">
                <Pencil className="h-4 w-4" />
                Edit Device
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={openDeleteModal} className="gap-2 text-destructive focus:text-destructive">
                <Trash2 className="h-4 w-4" />
                Delete Device
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
